import React, { useState, useEffect } from "react";
import { TextField, Paper, Typography, Tooltip, Popover, Box } from '@mui/material';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import { getFileExtension } from '../../Utils/fileUtils';
import { parseEventDate, formatForDateTimeLocal } from '../../Utils/dateUtils';

// Theme colors - matching Sidebar
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';
const lightPurple = '#f0f0ff';

const TimeEventDateDisplay = React.memo(({
    initialDate,
    onUpdateDate,
    timeevent,
    editable = true,
    displayValue = null,
}) => {
    const [thedate, setthedate] = useState(new Date(initialDate));
    const [originalDate, setOriginalDate] = useState(new Date(initialDate));
    const [formattedDate, setFormattedDate] = useState("");
    const [formattedTime, setFormattedTime] = useState("");
    const [isEditingDateTime, setIsEditingDateTime] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const hasDisplayValue = displayValue !== null && displayValue !== undefined;
    const normalizedDisplayValue = hasDisplayValue ? String(displayValue).trim() : '';
    const displayText = normalizedDisplayValue || '-';

    // Update 'thedate' whenever 'initialDate' changes
    useEffect(() => {
        const newDate = parseEventDate(initialDate);
        setthedate(newDate);
        setOriginalDate(newDate);
    }, [initialDate]);

    // Update formatted date and time whenever 'thedate' changes
    useEffect(() => {
        if (isNaN(thedate.getTime())) {
            setFormattedDate("-");
            setFormattedTime("");
        } else {
            const hours = thedate.getHours();
            const minutes = thedate.getMinutes();

            // Set the date string
            setFormattedDate(thedate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));

            // Only set the time string if it's not midnight
            setFormattedTime((hours !== 0 || minutes !== 0)
                ? thedate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : ""
            );
        }
    }, [thedate]);

    const handleDateTimeClick = (event) => {
        if (!editable) {
            return;
        }
        setOriginalDate(new Date(thedate));
        setAnchorEl(event.currentTarget);
        setIsEditingDateTime(true);
    };

    const handleDateTimeChange = (e) => {
        const newDate = parseEventDate(e.target.value);
        setthedate(newDate);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsEditingDateTime(false);
        // Only send update if the date is valid AND has actually changed
        if (!isNaN(thedate.getTime()) && thedate.getTime() !== originalDate.getTime()) {
            onUpdateDate(thedate); // Callback to parent component with local time
        }
    };

    // Convert the date to the local time format for the datetime-local input
    const getLocalDateTimeString = (date) => {
        return formatForDateTimeLocal(date);
    };

    return (
        <>
            <Tooltip title={hasDisplayValue ? displayText : (isNaN(thedate.getTime()) ? "Date Not Found" : thedate.toLocaleString())}>
                <Paper
                    elevation={3}
                    onClick={editable && !hasDisplayValue ? handleDateTimeClick : undefined}
                    sx={{
                        p: 0.5,
                        width: '100%',
                        backgroundColor: primaryColor,
                        color: "white",
                        textAlign: 'center',
                        cursor: editable && !hasDisplayValue ? 'pointer' : 'default',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        ...(editable && !hasDisplayValue ? {
                            '&:hover': {
                                backgroundColor: hoverColor,
                                transform: 'translateY(-2px)',
                                boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
                            }
                        } : {})
                    }}
                >
                    <div>
                        {hasDisplayValue ? (
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                noWrap
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {displayText}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                    {formattedDate}
                                </Typography>
                                {formattedTime && (
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                                        {formattedTime}
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>
                </Paper>
            </Tooltip>
            <Popover
                open={editable && isEditingDateTime}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: { p: 1 }
                }}
            >
                <TextField
                    type="datetime-local"
                    value={getLocalDateTimeString(thedate)}
                    onChange={handleDateTimeChange}
                    autoFocus
                    variant="outlined"
                    size="small"
                    sx={{ 
                        backgroundColor: '#ffffff',
                        minWidth: '220px',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: primaryColor,
                            },
                            '&:hover fieldset': {
                                borderColor: hoverColor,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: selectedColor,
                            },
                        },
                    }}
                />
            </Popover>
        </>
    );
});

export default TimeEventDateDisplay;
